import React from "react";
import { Button } from "@/components/ui/button";
import { RefreshCw, Loader2, AlertTriangle, Inbox } from "lucide-react";
import { money } from "@/lib/api";

export const crc = (n) => {
  const num = Number(n);
  if (n === "" || n === null || n === undefined || Number.isNaN(num)) return "—";
  return money(num);
};

export const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString("es-CR", { year: "numeric", month: "short", day: "numeric" });
};

export const cell = (v) => (v === "" || v === null || v === undefined ? "—" : String(v));

// Normalize phone values (often returned as numbers) to a plain string.
export const phone = (v) => (v === "" || v === null || v === undefined ? "—" : String(v));

export function LiveHeader({ title, subtitle, count, loading, onRefresh, children, testid }) {
  return (
    <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end sm:justify-between gap-3 mb-5 min-w-0" data-testid={testid}>
      <div className="min-w-0">
        <h1 className="font-display text-xl sm:text-2xl font-extrabold text-foreground break-words">{title}</h1>
        <p className="text-sm text-muted-foreground break-words">
          {subtitle}
          {typeof count === "number" && <span className="ml-1 font-semibold text-foreground">· {count} registros</span>}
          <span className="ml-2 inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 text-xs font-semibold whitespace-nowrap">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> live
          </span>
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto min-w-0">
        <div className="flex-1 sm:flex-none min-w-0">{children}</div>
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading} data-testid={`${testid}-refresh`} className="gap-1.5 shrink-0 min-h-10">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh
        </Button>
      </div>
    </div>
  );
}

export function StateBlock({ loading, error, empty, onRetry, children }) {
  if (loading)
    return (
      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground" data-testid="state-loading">
        <Loader2 className="h-7 w-7 animate-spin mb-3" /> Cargando datos en vivo…
      </div>
    );
  if (error)
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center px-4" data-testid="state-error">
        <AlertTriangle className="h-8 w-8 text-destructive mb-3" />
        <p className="text-destructive font-semibold max-w-md break-words">{error}</p>
        {onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry} className="mt-4">Reintentar</Button>
        )}
      </div>
    );
  if (empty)
    return (
      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground text-center px-4" data-testid="state-empty">
        <Inbox className="h-8 w-8 mb-3 opacity-50" /> No hay registros para mostrar.
      </div>
    );
  return children;
}

const STATUS_STYLES = {
  Cancelled: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  Pagada: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  VOID: "bg-slate-500/15 text-slate-600 dark:text-slate-300",
  Cancelada: "bg-rose-500/15 text-rose-600 dark:text-rose-400",
  Completada: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  Activa: "bg-secondary text-secondary-foreground",
  Parcial: "bg-amber-500/20 text-amber-600 dark:text-amber-400",
  Apartada: "bg-amber-500/20 text-amber-600 dark:text-amber-400",
  PENDING: "bg-rose-500/15 text-rose-600 dark:text-rose-400",
  "No Disponible": "bg-rose-500/15 text-rose-600 dark:text-rose-400",
  Disponible: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  alta: "bg-rose-500/15 text-rose-600 dark:text-rose-400",
  media: "bg-amber-500/20 text-amber-600 dark:text-amber-400",
  baja: "bg-secondary text-secondary-foreground",
};

const STATUS_LABELS = {
  // Legacy financial value. It means the balance is fully paid; it does NOT mean the order was cancelled.
  Cancelled: "Pagada",
  // Financial state becomes non-applicable when the commercial order is cancelled.
  VOID: "Sin efecto",
};

export function Badge({ value }) {
  if (value === "" || value === null || value === undefined) return <span className="text-muted-foreground">—</span>;
  const cls = STATUS_STYLES[value] || "bg-secondary text-secondary-foreground";
  const label = STATUS_LABELS[value] || String(value);
  return <span className={`inline-block text-xs font-bold px-2 py-0.5 rounded ${cls}`}>{label}</span>;
}
